import { useEffect, useRef, useState } from 'react';
import { ErreurReseau, appel } from '../api';

/**
 * « Relever la boîte » — demande au worker de lire tout de suite les nouveaux
 * messages de la boîte de candidatures, sans attendre son tour de veille IMAP.
 *
 * Le bouton vit dans la **barre de statut**, donc sur tous les écrans : la
 * demande arrive rarement quand on est sur la file d'ingestion, elle arrive
 * quand on attend une candidature annoncée par téléphone. Il reste aussi en
 * tête de la file d'ingestion, qui est l'écran où l'on en regarde le résultat.
 *
 * Le traitement est ASYNCHRONE : l'API met une commande en file et répond 202.
 * Le message de retour ne promet donc jamais un résultat — il dit que la
 * demande est partie, et `onDemande` rafraîchit les compteurs un instant plus
 * tard, quand le worker a eu le temps de travailler.
 */

/** Le temps laissé au worker avant de relire les compteurs. */
const DELAI_RELECTURE_MS = 4_000;
/** Durée d'affichage du message, aussi longue que le bouton reste au repos. */
const DUREE_MESSAGE_MS = 8_000;

type Etat = 'repos' | 'envoi' | 'demande';

export function RelancerLecture({
  className,
  onDemande,
}: {
  className?: string;
  /** Appelé après un court délai : recharger la liste, les compteurs… */
  onDemande?: () => void;
}) {
  const [etat, setEtat] = useState<Etat>('repos');
  const [erreur, setErreur] = useState<string | null>(null);
  // Les minuteurs sont annulés au démontage : sans cela, changer de page
  // pendant l'attente ferait travailler un composant qui n'existe plus.
  const minuteurs = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    const encours = minuteurs.current;
    return () => encours.forEach(clearTimeout);
  }, []);

  async function relancer(): Promise<void> {
    setErreur(null);
    setEtat('envoi');
    try {
      await appel('/ingestion/relancer', { method: 'POST' });
      setEtat('demande');
      minuteurs.current.push(
        setTimeout(() => onDemande?.(), DELAI_RELECTURE_MS),
        setTimeout(() => setEtat('repos'), DUREE_MESSAGE_MS),
      );
    } catch (e) {
      setEtat('repos');
      setErreur(
        e instanceof ErreurReseau && e.statut === null
          ? 'Hors ligne : la boîte sera relue au retour du réseau.'
          : e instanceof Error
            ? e.message
            : 'Relance impossible',
      );
    }
  }

  return (
    <span className={className ? `relance-boite ${className}` : 'relance-boite'}>
      <button
        type="button"
        className="bouton-relance"
        // Pendant l'envoi ET l'affichage du message : recliquer ne servirait à
        // rien (l'API ignore une seconde demande dans les 30 s) et laisserait
        // croire le contraire.
        disabled={etat !== 'repos'}
        onClick={() => void relancer()}
        title="Lire maintenant les nouveaux messages de la boîte de candidatures"
      >
        <IconeRelance tourne={etat === 'envoi'} />
        {etat === 'envoi' ? 'Relève…' : 'Relever la boîte'}
      </button>
      {/* `role="status"` : le message est annoncé sans voler le focus. */}
      <span className="relance-message" role="status">
        {erreur ?? (etat === 'demande' ? 'Demandé — les nouveautés arrivent dans un instant.' : '')}
      </span>
    </span>
  );
}

function IconeRelance({ tourne }: { tourne: boolean }) {
  return (
    <svg
      className={tourne ? 'icone-relance tourne' : 'icone-relance'}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}
